import { action } from '@ember/object';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import didInsert from '@ember/render-modifiers/modifiers/did-insert';
import Component from '@glimmer/component';
import type { Task } from 'ember-concurrency';
import { restartableTask, timeout } from 'ember-concurrency';
import perform from 'ember-concurrency/helpers/perform';
import UiSearch from 'garaje/components/ui-search';
import zft from 'garaje/utils/zero-for-tests';
import { localCopy } from 'tracked-toolbox';

interface BlocklistFiltersSearchInputSignature {
  Args: {
    /**
     * Name of input, used for testing only
     */
    name: string;
    /**
     * The value of the search-input
     */
    value: string;
    /**
     * Placeholder text for UI-INPUT component
     */
    placeholder: string;
    /**
     * When to show the clear button
     */
    showClear: boolean;
    /**
     * When to show the input itself
     */
    enabled: boolean;
    /**
     * Task to send filterText to parent component
     */
    onChange: Task<void, [string]>;
    title?: string;
  };
  Element: HTMLDivElement;
}

/**
 * Contextual component used for creating and editing basic BlocklistFilter information
 */
export default class BlocklistFiltersSearchInput extends Component<BlocklistFiltersSearchInputSignature> {
  @localCopy('args.value') value?: string;

  /**
   * Needed to ensure search input is focused
   */
  delayedFocus = restartableTask(async () => {
    const blocklistFilterSearchInput = document.getElementById('blocklist-search-input');
    if (blocklistFilterSearchInput) {
      await timeout(zft(500));
      blocklistFilterSearchInput.focus();
    }
  });

  /**
   * Clears the search text
   */
  @action
  clearSearchText(): void {
    this.value = '';
    void this.args.onChange.perform(this.value);
  }

  <template>
    <div ...attributes>
      {{#if @enabled}}
        <div class="slide-in slide-out" data-test-configure-blocklist-search={{@name}}>
          <div
            class="blocklist-search"
            {{didInsert this.delayedFocus.perform}}
            data-test-configure-blocklist-search-input
          >
            <UiSearch
              @inputAriaLabel={{@title}}
              @inputId="blocklist-search-input"
              @placeholder={{@placeholder}}
              @onInput={{perform @onChange value="target.value"}}
              @value={{this.value}}
              @showClearButton={{@showClear}}
              @onClear={{this.clearSearchText}}
            />
          </div>
        </div>
      {{/if}}
    </div>
  </template>
}
